import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyResponse, CompanySponsorResponse, EventResponse, UserEntityRelationWithUserDataResponse, FeedPostResponse, CompanyDocumentResponse, MembershipPlanResponse, MembershipResponse, CompanyImageResponse, MarketDealResponse } from '../../@shared/models/response.module';
import { CompanyService } from '../../@shared/services/company.service';
import { EventsService } from '../../@shared/services/events-service';
import { FeedService } from '../../@shared/services/feed.service';
import { GeocodingService } from '../../@shared/services/geocoding.service';
import { MapMarker, MapConfig } from '../../@shared/components/location-map/location-map';
import { StorageService } from '../../@core/services/storage.service';
import { EndpointService } from '../../@shared/endpoints.service';
import { COR_COY_0, AQH_EVE_0 } from '../../@shared/models/input.module';
import { formatDateDMYDots } from '../../@core/utils/utils.service';
import { SendMessageModalComponent } from '../../@shared/components/send-message-modal/send-message-modal.component';
import { PurchaseMembership } from '../user-profile/components/user-profile-membership/components/purchase-membership/purchase-membership';

@Component({
  selector: 'app-organisation-details',
  standalone: false,
  templateUrl: './organisation-details.html',
  styleUrl: './organisation-details.css'
})
export class OrganisationDetails implements OnInit {
  companyId: string = '';
  company: CompanyResponse | null = null;
  events: EventResponse[] = [];
  upcomingEvents: EventResponse[] = [];
  pastEvents: EventResponse[] = [];
  teamMembers: UserEntityRelationWithUserDataResponse[] = [];

  loading: boolean = false;
  loadingEvents: boolean = false;
  loadingTeam: boolean = false;

  activeTab: 'upcoming' | 'past' | 'all' = 'upcoming';

  descriptionExpanded: boolean = false;
  hasLongDescription: boolean = false;

  // Favorite properties
  isFavorite: boolean = false;
  favoriteLoading: boolean = false;

  // Map properties
  mapMarker: MapMarker | null = null;
  mapConfig: MapConfig = {
    height: '200px',
    mobileHeight: '180px',
    enableClustering: false,
    defaultZoom: 14,
    directionsMode: true
  };
  geocodingInProgress: boolean = false;

  // Feed properties
  feedPosts: FeedPostResponse[] = [];
  loadingFeed: boolean = false;

  // Sponsors properties
  sponsors: CompanySponsorResponse[] = [];
  loadingSponsors: boolean = false;

  // Documents properties
  documents: CompanyDocumentResponse[] = [];
  loadingDocuments: boolean = false;

  // Company images properties
  companyImages: CompanyImageResponse[] = [];
  loadingImages: boolean = false;

  // Membership properties
  membershipPlans: MembershipPlanResponse[] = [];
  userMemberships: MembershipResponse[] = [];
  loadingMembershipPlans: boolean = false;
  loadingUserMembership: boolean = false;

  // Market Deals properties
  deals: MarketDealResponse[] = [];
  loadingDeals: boolean = false;

  // Lightbox properties
  showLightbox: boolean = false;
  lightboxImages: string[] = [];
  lightboxIndex: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService,
    private eventsService: EventsService,
    private feedService: FeedService,
    private geocodingService: GeocodingService,
    private endpointService: EndpointService,
    private modalService: NgbModal,
    private storageService: StorageService
  ) {}

  get isLoggedIn(): boolean {
    return this.storageService.isLoggedIn();
  }

  ngOnInit() {
    this.companyId = this.route.snapshot.paramMap.get('id') || '';
    if (this.companyId) {
      this.loadCompany();
      this.loadEvents();
      this.loadTeam();
      this.loadFeedPosts();
      this.loadSponsors();
      this.loadDocuments();
      this.loadCompanyImages();
      this.loadMembershipPlans();
      this.loadDeals();
      if (this.isLoggedIn) {
        this.checkFavoriteStatus();
        this.checkUserMembership();
      }
    }
  }

  checkFavoriteStatus() {
    this.companyService.checkFavoriteStatus(this.companyId).subscribe({
      next: (response) => {
        this.isFavorite = response?.is_favorite || false;
      },
      error: () => {
        this.isFavorite = false;
      }
    });
  }

  toggleFavorite() {
    if (!this.isLoggedIn) {
      return;
    }

    this.favoriteLoading = true;

    if (this.isFavorite) {
      this.companyService.removeFavorite(this.companyId).subscribe({
        next: () => {
          this.isFavorite = false;
          this.favoriteLoading = false;
        },
        error: () => {
          this.favoriteLoading = false;
        }
      });
    } else {
      this.companyService.addFavorite(this.companyId).subscribe({
        next: () => {
          this.isFavorite = true;
          this.favoriteLoading = false;
        },
        error: () => {
          this.favoriteLoading = false;
        }
      });
    }
  }

  loadCompany() {
    this.loading = true;

    // Use public endpoint (no auth required)
    this.companyService.getPublicCompanyById(this.companyId).subscribe({
      next: (company) => {
        this.company = company;
        this.checkDescriptionLength();
        this.geocodeCompanyAddress();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  async geocodeCompanyAddress() {
    if (!this.company) return;
    if (!this.company.address && !this.company.city && !this.company.country) return;

    this.geocodingInProgress = true;

    const result = await this.geocodingService.geocodeAddress(
      this.company.address || '',
      this.company.city || '',
      this.company.postal_code || '',
      this.company.country || ''
    );

    if (result.success && result.lat && result.lng) {
      this.mapMarker = {
        id: this.company.company_id,
        lat: result.lat,
        lng: result.lng,
        title: this.company.company_name || 'Organisation Location',
        subtitle: `${this.company.city || ''}${this.company.city && this.company.country ? ', ' : ''}${this.company.country || ''}`,
        imageUrl: this.company.company_logo_url || this.company.header_image_url || undefined,
        data: this.company
      };
    }

    this.geocodingInProgress = false;
  }

  loadEvents() {
    this.loadingEvents = true;
    const params: AQH_EVE_0 = {
      company_id: this.companyId
    };

    // Use public endpoint (no auth required)
    this.eventsService.getPublicEvents(params).subscribe({
      next: (response) => {
        this.events = response.data || [];
        this.sortAndCategorizeEvents();
        this.loadingEvents = false;
      },
      error: () => {
        this.loadingEvents = false;
      }
    });
  }

  loadTeam() {
    this.loadingTeam = true;
    // Use public endpoint (no auth required)
    this.companyService.getPublicCompanyEmployees(this.companyId).subscribe({
      next: (employees) => {
        this.teamMembers = employees || [];
        this.loadingTeam = false;
      },
      error: () => {
        this.loadingTeam = false;
      }
    });
  }

  getMemberInitials(user: UserEntityRelationWithUserDataResponse): string {
    if (user.first_name && user.last_name) {
      return (user.first_name.charAt(0) + user.last_name.charAt(0)).toUpperCase();
    }
    if (user.full_name) {
      const parts = user.full_name.split(' ');
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
      }
      return user.full_name.substring(0, 2).toUpperCase();
    }
    return '?';
  }

  sortAndCategorizeEvents() {
    // Sort by date
    this.events.sort((a, b) => {
      const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
      const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.upcomingEvents = this.events
      .filter(event => {
        if (!event.start_date) return false;
        return new Date(event.start_date) >= today;
      })
      .sort((a, b) => {
        // Sort by start_date ascending (closest to today first)
        const dateA = new Date(a.start_date!).getTime();
        const dateB = new Date(b.start_date!).getTime();
        return dateA - dateB;
      });

    this.pastEvents = this.events
      .filter(event => {
        if (!event.start_date) return false;
        return new Date(event.start_date) < today;
      })
      .sort((a, b) => {
        // Sort by start_date descending (most recent past event first)
        const dateA = new Date(a.start_date!).getTime();
        const dateB = new Date(b.start_date!).getTime();
        return dateB - dateA;
      });
  }

  setActiveTab(tab: 'upcoming' | 'past' | 'all') {
    this.activeTab = tab;
  }

  getDisplayedEvents(): EventResponse[] {
    switch (this.activeTab) {
      case 'upcoming':
        return this.upcomingEvents;
      case 'past':
        return this.pastEvents;
      case 'all':
        return this.events;
    }
  }

  getCompanyInitials(): string {
    if (!this.company?.company_name) return '?';
    const words = this.company.company_name.split(' ');
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return this.company.company_name.substring(0, 2).toUpperCase();
  }

  getGoogleMapsUrl(): string {
    if (!this.company) return '';
    const addressParts = [
      this.company.address,
      this.company.postal_code,
      this.company.city,
      this.company.country
    ].filter(part => part);
    const fullAddress = addressParts.join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
  }

  checkDescriptionLength() {
    if (this.company?.description) {
      // Strip HTML tags to get plain text length
      const plainText = this.company.description.replace(/<[^>]*>/g, '');
      this.hasLongDescription = plainText.length > 300;
    }
  }

  toggleDescription() {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  openChatModal() {
    if (!this.company) return;

    const modalRef = this.modalService.open(SendMessageModalComponent, {
      size: 'lg',
      centered: true
    });
    modalRef.componentInstance.company = this.company;
  }

  hasSocialLinks(): boolean {
    if (!this.company) return false;
    return !!(
      this.company.website_url ||
      this.company.facebook_url ||
      this.company.instagram_url ||
      this.company.youtube_url ||
      this.company.tiktok_url ||
      this.company.twitter_url ||
      this.company.linkedin_url ||
      this.company.merchandise_url
    );
  }

  formatUrl(url: string | null | undefined): string {
    if (!url) return '';
    // If URL doesn't start with http:// or https://, add https://
    if (!url.match(/^https?:\/\//i)) {
      return 'https://' + url;
    }
    return url;
  }

  loadFeedPosts() {
    this.loadingFeed = true;
    this.feedService.getPublicFeedPosts(this.companyId).subscribe({
      next: (response) => {
        this.feedPosts = response.data || [];
        this.loadingFeed = false;
      },
      error: () => {
        this.loadingFeed = false;
      }
    });
  }

  openLightbox(post: FeedPostResponse, index: number) {
    this.lightboxImages = post.images.map(img => img.image_url);
    this.lightboxIndex = index;
    this.showLightbox = true;
  }

  closeLightbox() {
    this.showLightbox = false;
    this.lightboxImages = [];
    this.lightboxIndex = 0;
  }

  previousImage() {
    if (this.lightboxIndex > 0) {
      this.lightboxIndex--;
    } else {
      this.lightboxIndex = this.lightboxImages.length - 1;
    }
  }

  nextImage() {
    if (this.lightboxIndex < this.lightboxImages.length - 1) {
      this.lightboxIndex++;
    } else {
      this.lightboxIndex = 0;
    }
  }

  formatFeedDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  loadSponsors() {
    this.loadingSponsors = true;
    this.endpointService.cor_coy_26(this.companyId).subscribe({
      next: (response) => {
        this.sponsors = response.data || [];
        this.loadingSponsors = false;
      },
      error: () => {
        this.loadingSponsors = false;
      }
    });
  }

  shouldAnimateSponsors(): boolean {
    return this.sponsors.length > 3;
  }

  loadDocuments() {
    this.loadingDocuments = true;
    this.endpointService.cor_coy_31(this.companyId).subscribe({
      next: (response) => {
        this.documents = response.data || [];
        this.loadingDocuments = false;
      },
      error: () => {
        this.loadingDocuments = false;
      }
    });
  }

  loadCompanyImages() {
    this.loadingImages = true;
    this.endpointService.cor_coy_5({ company_id: this.companyId }).subscribe({
      next: (response) => {
        this.companyImages = response.data || [];
        this.loadingImages = false;
      },
      error: () => {
        this.loadingImages = false;
      }
    });
  }

  openImageLightbox(index: number) {
    this.lightboxImages = this.companyImages.map(img => img.image_url);
    this.lightboxIndex = index;
    this.showLightbox = true;
  }

  downloadDocument(document: CompanyDocumentResponse) {
    if (document.document_url) {
      window.open(document.document_url, '_blank');
    }
  }

  formatFileSize(bytes: number | null | undefined): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  loadMembershipPlans() {
    this.loadingMembershipPlans = true;
    this.endpointService.aqh_mem_11(this.companyId, false).subscribe({
      next: (response) => {
        this.membershipPlans = response.data || [];
        this.loadingMembershipPlans = false;
      },
      error: () => {
        this.membershipPlans = [];
        this.loadingMembershipPlans = false;
      }
    });
  }

  loadDeals() {
    this.loadingDeals = true;
    this.endpointService.cor_coy_55(this.companyId).subscribe({
      next: (response) => {
        this.deals = response.data || [];
        this.loadingDeals = false;
      },
      error: () => {
        this.deals = [];
        this.loadingDeals = false;
      }
    });
  }

  checkUserMembership() {
    this.loadingUserMembership = true;
    this.endpointService.aqh_mem_0({ company_id: this.companyId, active_only: true }, false).subscribe({
      next: (response) => {
        this.userMemberships = response.data || [];
        this.loadingUserMembership = false;
      },
      error: () => {
        this.userMemberships = [];
        this.loadingUserMembership = false;
      }
    });
  }

  get showBecomeMemberButton(): boolean {
    // Don't show if still loading
    if (this.loadingMembershipPlans) return false;
    // Don't show if no membership plans
    if (this.membershipPlans.length === 0) return false;
    // If logged in and already a member, don't show
    if (this.isLoggedIn && this.userMemberships.length > 0) return false;
    return true;
  }

  get isAlreadyMember(): boolean {
    return this.isLoggedIn && this.userMemberships.length > 0;
  }

  onBecomeMember() {
    if (!this.isLoggedIn) {
      // Redirect to login with return URL
      const returnUrl = `/organisations/${this.companyId}`;
      this.router.navigate(['/auth/login'], { queryParams: { returnUrl } });
    } else {
      // Open purchase membership modal
      const modalRef = this.modalService.open(PurchaseMembership, {
        size: 'lg',
        centered: true
      });
      modalRef.componentInstance.companyId = this.companyId;
      modalRef.componentInstance.company = this.company;

      // Refresh membership status when modal closes
      modalRef.result.then((result) => {
        if (result) {
          this.checkUserMembership();
        }
      }).catch(() => {});
    }
  }

  getFileIcon(extension: string | null | undefined): string {
    switch (extension?.toLowerCase()) {
      case 'pdf': return 'fa-file-pdf text-danger';
      case 'doc':
      case 'docx': return 'fa-file-word text-primary';
      case 'xls':
      case 'xlsx': return 'fa-file-excel text-success';
      case 'ppt':
      case 'pptx': return 'fa-file-powerpoint text-warning';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fa-file-image text-info';
      case 'zip':
      case 'rar': return 'fa-file-archive text-secondary';
      default: return 'fa-file text-muted';
    }
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;
}
